/*
 * Google Maps JS API loader.
 *
 * Uses Google's inline bootstrap loader pattern (https://developers.google.com/maps/documentation/javascript/load-maps-js-api#dynamic-library-import)
 * so that callers can use `google.maps.importLibrary('places')` to load any
 * Maps library on demand. The new Places API (PlaceAutocompleteElement, the
 * Place class) is only available through this path.
 *
 * `loadGoogleMaps(apiKey)` installs the bootstrap on first call and resolves
 * immediately — the actual Maps JS script is fetched lazily the first time a
 * caller invokes `google.maps.importLibrary(...)`, with the union of all
 * requested libraries.
 */

type ImportLibrary = <T = unknown>(name: string) => Promise<T>;

type MapsRoot = {
  importLibrary?: ImportLibrary;
  __ib__?: () => void;
};

type GlobalWithGoogle = {
  google?: { maps?: MapsRoot };
};

let bootstrapped = false;

export function loadGoogleMaps(apiKey: string): Promise<void> {
  if (!bootstrapped) {
    bootstrapLoader({ key: apiKey, v: 'weekly' });
    bootstrapped = true;
  }

  // The bootstrap itself is synchronous; the Maps JS script fetch happens
  // lazily on the first `importLibrary(...)` call. Callers should chain on
  // that, not on this promise — we return resolved so existing call-sites
  // stay shape-compatible.
  return Promise.resolve();
}

function bootstrapLoader(params: { readonly key: string; readonly v: string }): void {
  const win = globalThis as unknown as GlobalWithGoogle;
  const google = (win.google ??= {});
  const maps: MapsRoot = (google.maps ??= {});

  if (maps.importLibrary !== undefined) return;

  const requested = new Set<string>();
  let scriptPromise: Promise<void> | null = null;

  const ensureScript = (): Promise<void> => {
    if (scriptPromise !== null) return scriptPromise;

    scriptPromise = new Promise<void>((resolve, reject) => {
      // Yield one microtask so synchronous importLibrary() calls all add to
      // the requested set before we build the URL.
      void Promise.resolve().then(() => {
        const search = new URLSearchParams();
        search.set('libraries', [...requested].join(','));

        for (const [rawKey, value] of Object.entries(params)) {
          const key = rawKey.replace(/[A-Z]/g, (c) => '_' + c.toLowerCase());
          search.set(key, value);
        }

        search.set('callback', 'google.maps.__ib__');

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?${search.toString()}`;
        script.async = true;
        maps.__ib__ = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google Maps JS API'));
        document.head.append(script);
      });
    });

    return scriptPromise;
  };

  // Stub importLibrary — Maps JS replaces this with the real implementation
  // once it finishes initialising; the recursive call below then routes
  // through the real one.
  maps.importLibrary = (async (name: string) => {
    requested.add(name);
    await ensureScript();

    return (maps.importLibrary as ImportLibrary)(name);
  }) as ImportLibrary;
}
